import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  output,
  signal,
  untracked,
  ViewChild,
  WritableSignal,
} from "@angular/core";
import { JobStateService } from "../job-state.store";
import { WellLog } from "../../../../models/wellLog";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  catchError,
  EMPTY,
  forkJoin,
  map,
  NEVER,
  of,
  switchMap,
  tap,
} from "rxjs";
import { ImageLoaderService } from "../../../../shared/services/image-loader.service";
import { SliceType } from "../../../../models/sliceType";
import { ViewerSettingsService } from "../viewer-settings.store";
import { BatchScreenshotService } from "../batch-screenshot.service";
import { NzModalService } from "ng-zorro-antd/modal";
import { MinMaxDialogComponent } from "../min-max-dialog/min-max-dialog.component";
import { VerticalProfileService } from "../vertical-profile/vertical-profile.service";
import { ModelViewerSettingsService } from "../model-viewer-settings.service";
import { LogMarkers } from "../../../../models/coordinate";
import { MyViewerComponent } from "../my-viewer/my-viewer.component";
import { SearchableInputListPipe } from "../../../../shared/pipes/searchable-input-list.pipe";
import {
  G_ProjectHorizon,
  GeneralizedHorizon,
  HorizonWithData,
} from "../../../../models/generalizedHorizon";

const M_TO_FT = 1 / 0.3048;

export interface SonicDataResourceRes {
  data: { depth: number; velocity: number }[];
  x: number;
  markers: LogMarkers[];
}

@Component({
  selector: "app-inline-slice",
  standalone: false,
  templateUrl: "./inline-slice.component.html",
  styleUrl: "./inline-slice.component.less",
})
export class InlineSliceComponent {
  @ViewChild("sliceViewer") viewer!: MyViewerComponent;
  @ViewChild("modelSearchInput") private modelSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild("colorMapInput") private colorMapInput!: ElementRef<HTMLInputElement>;

  onScreenshot = output<void | string>();

  protected modelViewerSettings = inject(ModelViewerSettingsService);
  protected viewerSettings = inject(ViewerSettingsService);
  protected jobState = inject(JobStateService);
  protected verticalProfileService = inject(VerticalProfileService);
  protected is2D = computed(() => this.jobState.jobData().modelGrid.is2d);

  private cachedProjectService = inject(CachedProjectService);
  private cachedImageService = inject(ImageLoaderService);
  private nzModalService = inject(NzModalService);

  protected readonly displayData = signal<any>(null);

  inlineMax = computed(
    () =>
      this.jobState.inlineDx() *
      (this.jobState.jobData().x2_is_inline
        ? this.jobState.jobData().modelGrid.nx2
        : this.jobState.jobData().modelGrid.nx1),
  );
  crosslineMax = computed(
    () =>
      this.jobState.crosslineDx() *
      (this.jobState.jobData().x2_is_inline
        ? this.jobState.jobData().modelGrid.nx1
        : this.jobState.jobData().modelGrid.nx2),
  );
  depthMax = computed(
    () => this.jobState.dx3() * this.jobState.jobData().modelGrid.nx3,
  );

  xCrop = computed(() => [
    this.jobState.rangesStart.XL(),
    this.jobState.rangesEnd.XL(),
  ]);
  yCrop = computed(() => [
    this.jobState.rangesStart.depth(),
    this.jobState.rangesEnd.depth(),
  ]);

  private searchableInputListPipe = new SearchableInputListPipe();

  protected propertySearch = signal("");
  filteredModels() {
    return this.searchableInputListPipe.transform(
      this.jobState.availableModelsResource.value() ?? [],
      this.propertySearch(),
    );
  }

  protected colorMapSearch = signal("");
  filteredColorMaps() {
    return this.searchableInputListPipe.transform(
      this.jobState.colorMapResource.value() ?? [],
      this.colorMapSearch(),
    );
  }

  protected highlightedModelIndex = signal(-1);
  protected highlightedColorMapIndex = signal(-1);

  onModelPropertyOpenChange(open: boolean) {
    if (open) {
      setTimeout(() => this.modelSearchInput?.nativeElement.focus());
    }
  }

  onColorPropertyOpenChange(open: boolean) {
    if (open) {
      setTimeout(() => this.colorMapInput?.nativeElement.focus());
    }
  }

  onPropertySearchKeydown(event: KeyboardEvent) {
    const models = this.filteredModels();
    if (!models.length) return;

    switch (event.key) {
      case "ArrowDown":
        this.highlightedModelIndex.update((i) => (i + 1) % models.length);
        break;
      case "ArrowUp":
        this.highlightedModelIndex.update(
          (i) => (i - 1 + models.length) % models.length,
        );
        break;
      case "Enter": {
        const model = models[this.highlightedModelIndex()] ?? models[0];
        this.onSelectModelHandler(model);
        break;
      }
    }
  }

  onColorMapSearchKeydown(event: KeyboardEvent) {
    const colorMaps = this.filteredColorMaps();
    if (!colorMaps.length) return;

    switch (event.key) {
      case "ArrowDown":
        this.highlightedColorMapIndex.update((i) => (i + 1) % colorMaps.length);
        break;
      case "ArrowUp":
        this.highlightedColorMapIndex.update(
          (i) => (i - 1 + colorMaps.length) % colorMaps.length,
        );
        break;
      case "Enter": {
        const cmap = colorMaps[this.highlightedColorMapIndex()] ?? colorMaps[0];
        this.onSelectCmapHandler(cmap);
        break;
      }
    }
  }

  wellLogs = computed(() => this.jobState.jobData().wellLogs);
  selectedHorizonIds = computed(
    () => new Set(this.jobState.selectedHorizon().map((h) => h.id)),
  );

  onSelectWellLogHandler(wellLog: WellLog) {
    if (
      this.jobState.selectedLog() &&
      this.jobState.selectedLog().id == wellLog.id
    )
      this.jobState.selectedLog.set(null);
    else this.jobState.selectedLog.set(wellLog);
  }

  private getHorizonColor(index: number = null){
    const palette = ['#3498db', '#e74c3c', '#2ecc71', '#f1c40f', '#9b59b6'];
    if (index !== null) {
      return palette[index % palette.length]
    }
    else{
      return palette[Math.floor(Math.random() * palette.length)];
    }
  }
  onSelectHorizonHandler(horizon: GeneralizedHorizon) {
    let selectedHorizons = this.jobState.selectedHorizon();
    let indexOfHorizon = selectedHorizons.findIndex((h) => h.id === horizon.id);
    if (indexOfHorizon > -1) {
      selectedHorizons.splice(indexOfHorizon, 1);
    } else {
      selectedHorizons.push({...horizon, color: this.getHorizonColor(selectedHorizons.length)});
    }
    this.jobState.selectedHorizon.set([...selectedHorizons]);
  }

  onSelectCmapHandler(cmap: string) {
    this.jobState.selectedCmap.set(cmap);
    this.colorMapSearch.set("");
  }
  onSelectModelHandler(modelProperty: string) {
    this.jobState.selectedModel.set(modelProperty);
    this.propertySearch.set("")
  }
  onSelectShotHandler = this.jobState.selectedShot.set;

  sliceNumber = this.jobState.jobData().x2_is_inline
    ? SliceType.inline
    : SliceType.crossLine;

  sonicFrequeny: number = 0;
  sonicFrequencyChange(event: any) {
    this.jobState.logFrequency.set(this.sonicFrequeny);
  }

  setVpRef(event: Event) {
    let el = event.target as HTMLInputElement;
    let vpRef = Number(el.value) ?? 0;
    this.jobState.vpRef.set(vpRef);
  }

  setAGCWindow(event: Event) {
    let el = event.target as HTMLInputElement;
    let rawValue = parseFloat(el.value);
    if (isNaN(rawValue)) {
      rawValue = this.jobState.jobData().modelGrid.nx3 / 10;
    }
    // Force integer
    const agcWindow = Math.round(rawValue);
    this.jobState.AGCWindow.set(agcWindow);
  }

  // this should re run whenever the inlneMinMax changes. Auto is slightly lesser relevant in this case
  // the equal operator has been added because the inlineMinMax value is set after the sliceResource emits
  // however, it is also set when the min max dialog sets the value
  // so sometimes, it might run twice
  // while this causes very small latency if any even, adding the equal does add an additional level of checking the value
  private minMaxComputed = computed(
    () => {
      const settings = this.viewerSettings;
      const current = settings.inlineMinMax();
      const auto = untracked(() => settings.auto());
      return auto ? { min: null, max: null } : current;
    },
    {
      equal: (a, b) => a.min == b.min && a.max == b.max,
    },
  );

  private effectiveVpRef = computed(() => {
    // Only return the ref if the model type matches
    const model = this.jobState.selectedModel();
    return model === "Total_Vp_Update" ? this.jobState.vpRef() : null;
  });

  private effectiveAGCWindow = computed(() => {
    const model = this.jobState.selectedModel();
    return model === "DenChange AGC" ||
      model === "Density plus DenChange Reflectivity AGC"
      ? this.jobState.AGCWindow()
      : null;
  });

  // 3. The Image + Bins Resource
  protected sliceResource = rxResource({
    // 'params' combines Global Store signals + Local Component signals
    params: () => ({
      job: this.jobState.jobData(),
      inline: this.jobState.inline(), // from Store
      iteration: this.jobState.iteration(), // from Store
      modelType: this.jobState.selectedModel(), // from Store
      cmap: this.jobState.selectedCmap(), // from Store
      min: this.minMaxComputed().min,
      max: this.minMaxComputed().max,
      vpRef: this.effectiveVpRef(),
      window: this.effectiveAGCWindow(),
      shot: this.jobState.selectedShot(),
    }),
    stream: ({ params }) => {
      // Logic gate: only fetch if we have a valid Job ID and Slice number
      if (!params.job) return EMPTY;
      // if its a shot type model, but shot is not chosen?
      if (params.modelType == "grad" && !params.shot) return NEVER;
      // Fetch both Image (Blob) and Bins (JSON) in parallel
      return this.cachedProjectService
        .getSlice(
          params.job.id,
          params.modelType,
          params.iteration,
          this.sliceNumber, // inline = 1, crossline = 2, depth = 3 [computed to resolve the assumption of x2IsInline]. also, this is untracked because this is the 'sliceType' really. so within the inline component this won't really ever change
          params.inline,
          params.job.modelGrid.is2d,
          params.cmap,
          params.min, // minVal,
          params.max, //maxVal,
          params.shot, // this.shot,
          1,
          params.vpRef, // this.isVpReference ? this.vpIteration : 0, VP Ref for comparison models
          params.window,
          null, // this.timeSlice
        )
        .pipe(
          switchMap((res) => {
            const { sliceUrl, url, colorBarUrl, trueMax, trueMin, max, min } =
              res;

            untracked(() => {
              this.viewerSettings.setTrueLimits("inline", trueMax, trueMin);
              // set the inline min max
              // there is perhaps no need of NOT setting it really?
              // it should just ideally have the current value
              // no reason to have multiple states, keep it fair and simple
              // and this itself can be passed to the viewer?
              this.viewerSettings.inlineMinMax.set({ max, min });
            });

            // saving settings in cache for persistence
            let settings = {
              minMax: { min, max },
              cmap: params.cmap,
              auto: this.viewerSettings.auto(),
            };

            if (params.modelType == "Total_Vp_Update")
              settings["vpRef"] = params.vpRef;

            this.modelViewerSettings.saveModelSettings(
              params.job.id,
              params.modelType,
              "inline",
              settings,
            );

            return forkJoin({
              sliceImage: this.cachedImageService.getImageBase64(url),
              // colorbar: this.cachedImageService.getImageBase64(colorBarUrl),
              colorbar: of(colorBarUrl),
              sliceArray: this.cachedProjectService
                .getSliceArray(
                  sliceUrl,
                  params.job.modelGrid.nx2,
                  params.job.modelGrid.nx3,
                  trueMin,
                  trueMax,
                )
                .pipe(
                  tap((data) =>
                    this.verticalProfileService.inlineSliceData.set(data),
                  ),
                ), // the verticalProfile data is also set here.
              trueMax: of(trueMax),
              trueMin: of(trueMin),
            });
          }),
          catchError((err) => {
            throw Error(
              err.error?.detail ||
                err.message ||
                "An unexpected error occurred",
            );
          }),
        );
    },
  });

  // Stable computed that ignores color changes — only re-evaluates when horizon IDs/types or slice params change
  private horizonsForFetch = computed(
    () => ({
      keys: this.jobState.selectedHorizon().map((h) => ({ id: h.id, type: h.type })),
      sliceNumber: this.jobState.jobData().x2_is_inline
        ? this.jobState.crossline()
        : this.jobState.inline(),
      sliceType: this.jobState.jobData().x2_is_inline
        ? SliceType.inline
        : SliceType.crossLine,
    }),
    {
      equal: (a, b) =>
        a.sliceNumber === b.sliceNumber &&
        a.sliceType === b.sliceType &&
        a.keys.length === b.keys.length &&
        a.keys.every((h, i) => h.id === b.keys[i].id && h.type === b.keys[i].type),
    },
  );

  // 4. Horizon data resources. will be in conjunction with selectedProjectHorizons
  horizonsDataResource = rxResource<HorizonWithData[], ReturnType<typeof this.horizonsForFetch>>({
    params: () => this.horizonsForFetch(),
    stream: ({ params }) => {
      if (params.keys.length == 0) return of([]);

      const { sliceNumber, sliceType, keys } = params;
      const jobId = this.jobState.jobData().id;
      const selectedHorizon = untracked(() => this.jobState.selectedHorizon());
      const requests = keys.map((key, i) => {
        const h = selectedHorizon[i];
        const fetch$ =
          key.type === "Project"
            ? this.cachedProjectService.getProjectHorizonById(key.id, sliceNumber, sliceType)
            : this.cachedProjectService.getHorizon(jobId, sliceNumber, sliceType, key.id);
        return fetch$.pipe(map((data) => ({ ...h, data })));
      });
      return forkJoin(requests);
    },
  });

  // 5. Sonic log resources. will be in conjuntion with selectedSonic
  sonicLogDataResource = rxResource<
    SonicDataResourceRes,
    { selectedSonic: WellLog; sonicFrequency: number }
  >({
    params: () => ({
      selectedSonic: this.jobState.selectedLog(),
      sonicFrequency: this.jobState.logFrequency(),
    }),
    stream: ({ params }) => {
      if (!params.selectedSonic) return of(null);
      else {
        return this.cachedProjectService
          .getSonicLog(params.selectedSonic.id, params.sonicFrequency)
          .pipe(
            map((res) => {
              return {
                data: res.log.map((dp) => ({ depth: dp.x, velocity: dp.y })),
                x: params.selectedSonic.crossline - 1, // the -1 is used to position corectly. its also used when assigning the location. It will perhaps be relevant to figure how to actually save them because it is quite confusing right now
                markers: res.markers,
              };
            }),
          );
      }
    },
  });

  // 6. available shots resource. this is mostly only for scenarios when the model selected is that of type shot. e.g. grad
  protected availableIterationShots = rxResource<any, any>({
    params: () => ({
      modelType: this.jobState.selectedModel(),
      jobId: this.jobState.jobData().id,
      iteration: this.jobState.iteration(),
    }),
    stream: ({ params }) => {
      const { jobId, modelType, iteration } = params;
      if (modelType !== "grad") return;
      // only runs in the scenario of being a grad
      // the logic in the tap simply runs for setting selectedShot. it runs always cause this section runs // reruns iff model
      return this.cachedProjectService
        .getShotsAvailableForModel(jobId, iteration, modelType)
        .pipe(
          map((shots) => shots.sort()),
          tap(
            (shots) =>
              !this.jobState.selectedShot() &&
              this.onSelectShotHandler(shots[0]),
          ),
        );
    },
  });

  // Clean up Object URLs to prevent memory leaks
  constructor() {
    // report image loading to the batch screenshot runner so it knows when the
    // viewer has settled after a job/iteration change
    const batchScreenshotService = inject(BatchScreenshotService);
    effect(() => batchScreenshotService.reportSliceLoading("inline", this.sliceResource.isLoading()));
    inject(DestroyRef).onDestroy(() => batchScreenshotService.reportSliceLoading("inline", false));

    effect((onCleanup) => {
      if (this.sliceResource.error()) return;

      const data: { url: string; bins: any } =
        this.sliceResource.value() as any;
      if (data?.url) {
        onCleanup(() => URL.revokeObjectURL(data.url));
      }
    });

    effect(() => {
      if (this.sliceResource.error()) return;

      const val = this.sliceResource.value();
      if (val) {
        this.displayData.set(val);
      }
    });
  }

  backToPreviousModel() {
    this.jobState.selectedModel.set(this.jobState.previouslySelectedModel());
  }

  openMinMaxDialog() {
    this.nzModalService.create({
      nzContent: MinMaxDialogComponent,
    });
  }

  log(args) {
    console.log(args);
  }

  // ranges and dx are stored in metres. their inputs show them in m or ft to match the axis units
  protected rangeUnit = computed(() =>
    this.viewerSettings.axisUnits() === "km" ? "m" : "ft",
  );

  // converts an input value from the display unit to metres, the unit ranges and dx are stored in
  private toMetres(value: number) {
    return this.rangeUnit() === "m" ? value : value / M_TO_FT;
  }

  protected toRangeUnit(metres: number) {
    return this.rangeUnit() === "m" ? metres : Math.round(metres * M_TO_FT);
  }

  protected setDx(axis: "dx1" | "dx2" | "dx3", value: number) {
    if (value > 0) this.jobState.setDx(axis, this.toMetres(value));
  }

  setRange(
    event: Event,
    signal_to_set: WritableSignal<number>,
    _default: number = null, // in metres
  ) {
    let el = event.target as HTMLInputElement;
    let val = Number(el.value);
    if (!val && _default !== null) {
      // here we set the default, if availble for the field
      // this is to allow working with the nzMax scenario
      signal_to_set.set(_default);
      return;
    }
    if (!isNaN(val)) {
      signal_to_set.set(this.toMetres(val));
    }
  }

  resetZoom() {
    this.viewer.resetZoom();
  }

  protected setFullWidth(value: boolean) {
    if (value) {
      if (this.viewerSettings.verticalProfileMode() === "besideInline")
        this.viewerSettings.verticalProfileMode.set("show");
    }
  }
  protected setProfileBesideInline(value: boolean) {
    if (value) {
      this.viewerSettings.fullWidthIlXl.set(false);
    }
  }
}
